import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';
import { lockScroll } from '@theme/utilities';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#makeModalAtEveryWidth();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#dialog?.addEventListener('close', this.#handleDialogClosedElsewhere);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#dialog?.removeEventListener('close', this.#handleDialogClosedElsewhere);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
  }

  /**
   * MÂZIA: the bag is an overlay with a scrim at every width (spec 06 §8.1). `<theme-drawer>` opens drawers
   * with the non-modal `show()` from 990px (its page "squeeze" sidebar); for this dialog `show()` opens it
   * modally instead, so it gets the ::backdrop scrim, the inert page and the native focus trap, and the page
   * scroll is locked as below 990px. The lock is keyed to the panel, which `<theme-drawer>` unlocks on close.
   * The squeeze margin itself is reset in assets/mz-cart.css.
   */
  #makeModalAtEveryWidth() {
    const dialog = this.#dialog;
    if (!dialog || dialog.dataset.mzModal) return;

    dialog.dataset.mzModal = 'true';
    dialog.show = () => {
      if (!dialog.open) HTMLDialogElement.prototype.showModal.call(dialog);
      lockScroll(dialog);
    };
  }

  /**
   * Keeps `<theme-drawer>` in step when something else closes the dialog: Razorpay Magic Checkout calls
   * `close()` on every dialog on the page when its checkout opens. Without this the drawer would stay marked
   * open, with the page scroll locked, after the shopper leaves Razorpay's sheet.
   */
  #handleDialogClosedElsewhere = () => {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;
    if (this.#themeDrawer?.isOpen) this.#themeDrawer.close();
  };

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    // MÂZIA: on phones a quick add from a product card shows the "Added to bag" toast instead of opening the
    // bag (spec 06 §8.1). assets/mz-cart.js flags those events in a window capture listener, before this one.
    const showsToast = /** @type {{ mzBagToast?: boolean }} */ (/** @type {unknown} */ (event)).mzBagToast === true;
    const shouldAutoOpen =
      this.hasAttribute('auto-open') && event.action === 'add' && !showsToast && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
